import { IJSONSchema } from "../../../base/common/jsonSchema";
import { NeedleMeta } from "../../../base/common/needle";
import { safeStringify } from "../../../base/common/objects";
import { IHierarchyNode, IHierarchyService } from "../../../platform/hierarchy/common/hierarchy";
import { IInstantiationService } from "../../../platform/instantiation/common/instantiation";
import { ILogService } from "../../../platform/log/common/log";
import { AIToolCallResponse, FunctionAiTool } from "../../../platform/ai/common/ai";
import { aiTargetFor, type TargetResolverLike } from "../../../platform/ai/common/target";
import { safeStringifyReplacer } from "../../../platform/ai/common/tools/propertyTools";
import { Needle } from "../../../platform/webgl/three.js/propertyProvider";
import { displayComponentName } from "../../../platform/webgl/three.js/componentName";
import { IObjectTargetService } from "../project/common/documentTarget";
import { IComponentsService } from "../components/common/componentsService";
import { ICommandService } from "../../../platform/commands/common/commands";


type ComponentSearchParams = {
    query?: string;
    nodeQuery?: string;
    maxResults?: number;
    includeProperties?: boolean;
    maxValueSize?: number;
}

export class ComponentSearchTool extends FunctionAiTool<ComponentSearchParams> {
    readonly name = "component_search";
    readonly description = "Search for Needle Engine components in the scene. Finds nodes that have components matching the query (by component type name, e.g. 'OrbitControls', 'Camera', 'Light.*'). Returns nodes with their matched components. Optionally narrows the search to specific nodes by name using nodeQuery.";
    readonly parametersJSONSchema: IJSONSchema = {
        type: "object",
        properties: {
            query: {
                type: "string",
                description: "Regex pattern to match component type names (e.g. 'OrbitControls', 'Camera', 'Light.*', 'WebXR|Avatar'). Case-insensitive. If not provided, returns all components on matched nodes.",
            },
            nodeQuery: {
                type: "string",
                description: "Optional search query to narrow down to specific nodes by name first (e.g. 'player' to only search nodes with 'player' in their name). Multiple terms can be separated by commas.",
            },
            maxResults: {
                type: "number",
                description: "Maximum number of nodes to return. Default is 50.",
                default: 50,
            },
            includeProperties: {
                type: "boolean",
                description: "Whether to include component properties in the response. Default is false. When true, returns property names, types, and values for each matched component.",
                default: false,
            },
            maxValueSize: {
                type: "number",
                description: "When includeProperties is true, limits the maximum character length of each stringified property value. Longer values are truncated. Default is 200.",
                default: 200,
            },
        },
        required: [],
    }

    constructor(
        @IHierarchyService private readonly hierarchy: IHierarchyService,
        @ILogService private readonly logger: ILogService,
        @IInstantiationService private readonly instantiation: IInstantiationService,
    ) {
        super();
    }

    /** lazily resolved — absent outside the document products (chrome), where
     *  the target seam degrades to ref-less graph addresses */
    private get targets(): TargetResolverLike | undefined {
        try {
            return this.instantiation.invokeFunction(accessor => accessor.get(IObjectTargetService)) as TargetResolverLike;
        } catch {
            return undefined;
        }
    }

    /** registered component TYPES (the Add list) matching the query — lazily
     *  resolved like `targets`; empty where no components service exists */
    private registeredTypeMatches(regex: RegExp): string[] {
        try {
            const components = this.instantiation.invokeFunction(accessor => accessor.get(IComponentsService));
            const options = components.getComponentOptions()?.options ?? [];
            return options
                .map(option => (option as { name?: string; id: string }).name ?? option.id)
                .filter(name => !!name && regex.test(name))
                .slice(0, 10);
        }
        catch {
            return [];
        }
    }

    /** the SAME code search the AI's project.searchFiles runs (one
     *  implementation, entry paths invoke) — [] where file commands don't
     *  exist (chrome) or no project is open */
    private async projectFileMatches(query: string): Promise<Array<{ path: string; line: number; text: string }>> {
        try {
            const commands = this.instantiation.invokeFunction(accessor => accessor.get(ICommandService));
            const outcome = await commands.executeCommand<string>("project.searchFiles", { query, isRegex: true, maxResults: 10 });
            if (!outcome?.success || typeof outcome.result !== "string") return [];
            const parsed = JSON.parse(outcome.result) as { matches?: Array<{ path: string; line: number; text: string }> };
            return parsed.matches ?? [];
        }
        catch {
            return [];
        }
    }

    async call(parameters?: ComponentSearchParams): Promise<AIToolCallResponse> {

        if (!this.hierarchy) {
            return {
                result: {
                    message: "No project or scene hierarchy available.",
                    success: false,
                }
            }
        }

        const targets = this.targets;
        const queryRegex = parameters?.query ? new RegExp(parameters.query, "i") : null;
        const maxResults = parameters?.maxResults ?? 50;
        const includeProperties = parameters?.includeProperties ?? false;
        const maxValueSize = Math.max(10, parameters?.maxValueSize ?? 200);

        // Determine which nodes to search
        let nodes: Iterable<IHierarchyNode>;
        let nodeSearchInfo: { query: string; nodesFound: number } | undefined;

        if (parameters?.nodeQuery) {
            const terms = parameters.nodeQuery.split(',').map(q => q.trim()).filter(q => q.length > 0);
            const found = await this.hierarchy.search(terms, maxResults * 3);
            nodes = found;
            nodeSearchInfo = { query: parameters.nodeQuery, nodesFound: found.length };
        } else {
            nodes = this.hierarchy.iterateNodes();
        }

        const results: unknown[] = [];

        for (const node of nodes) {
            if (results.length >= maxResults) break;

            const components = Needle.getNeedleComponents(node.owner);
            if (components.runtimeComponents.length === 0 && components.gltfComponents.length === 0) {
                continue;
            }

            const matchedComponents: Array<{
                name: string;
                type: "runtime" | "gltf";
                enabled?: boolean;
                description?: string | null;
                properties?: unknown[];
            }> = [];

            // Check runtime components
            for (const comp of components.runtimeComponents) {
                const name = displayComponentName(comp);
                if (queryRegex && !queryRegex.test(name)) continue;

                const entry: typeof matchedComponents[0] = {
                    name,
                    type: "runtime",
                    enabled: comp.enabled,
                    description: NeedleMeta.getTypeDescriptionShort(name),
                };

                if (includeProperties) {
                    entry.properties = collectRuntimePropertySummary(comp, maxValueSize);
                }

                matchedComponents.push(entry);
            }

            // Check glTF components
            for (const comp of components.gltfComponents) {
                const name = comp.name;
                if (queryRegex && !queryRegex.test(name)) continue;

                const entry: typeof matchedComponents[0] = {
                    name,
                    type: "gltf",
                    enabled: comp.enabled,
                    description: NeedleMeta.getTypeDescriptionShort(name),
                };

                if (includeProperties) {
                    entry.properties = collectGltfPropertySummary(comp, maxValueSize);
                }

                matchedComponents.push(entry);
            }

            if (matchedComponents.length > 0) {
                results.push({
                    // the document TARGET (1h) — passed verbatim to the property
                    // tools; node ids are session-internal and never leave
                    target: node.owner && typeof node.owner === "object" ? aiTargetFor(targets, this.hierarchy, node.owner) ?? undefined : undefined,
                    nodeName: node.name,
                    nodeType: node.type,
                    components: matchedComponents,
                });
            }
        }

        const responseData: any = {
            totalReturned: results.length,
            filters: {
                query: parameters?.query,
                nodeQuery: parameters?.nodeQuery,
                includeProperties,
            },
            results,
        };

        if (nodeSearchInfo) {
            responseData.nodeSearch = nodeSearchInfo;
        }

        // A bare 0 reads as "does not exist" — but a project-code component can
        // be REGISTERED without being attached anywhere, or sit only in the
        // FILES (unbuilt code). "find the rotate slowly component" answered 0
        // while RotateSlowly sat in main.ts (live 2026-08-31) — the ONE search
        // must surface both.
        if (queryRegex) {
            const registered = this.registeredTypeMatches(queryRegex);
            if (registered.length > 0) {
                responseData.registeredTypes = registered;
                if (results.length === 0) {
                    responseData.hint = "These component TYPES are registered (available to add) but attached to no node — attach with needle_component_add {target, componentType}.";
                }
            }
            if (results.length === 0) {
                const fileMatches = await this.projectFileMatches(parameters!.query!);
                if (fileMatches.length > 0) {
                    responseData.fileMatches = fileMatches;
                    const files = [...new Set(fileMatches.map(m => m.path))].join(", ");
                    responseData.hint = `${responseData.hint ? responseData.hint + " " : ""}Project code matches in ${files} — read with project_readFile; unregistered classes register once the project code builds.`;
                }
            }
        }

        const aiMessage = safeStringify(responseData, undefined, safeStringifyReplacer);

        return {
            result: {
                message: responseData,
                aiMessage,
                success: true,
            }
        }
    }
}


function collectRuntimePropertySummary(comp: Needle.MaybeNeedleRuntimeComponent, maxValueSize: number): unknown[] {
    const properties: unknown[] = [];
    const types = comp.$serializedTypes;
    if (!types) return properties;

    for (const key in types) {
        if (key === "enabled") continue;
        const value = comp[key];
        properties.push({
            key,
            type: typeof value,
            value: clampValue(value, maxValueSize),
        });
    }
    return properties;
}

function collectGltfPropertySummary(comp: Needle.NeedleGltfComponent, maxValueSize: number): unknown[] {
    const properties: unknown[] = [];
    for (const key of Object.keys(comp)) {
        if (key === "name" || key === "guid" || key === "enabled") continue;
        const value = comp[key];
        properties.push({
            key,
            type: typeof value,
            value: clampValue(value, maxValueSize),
        });
    }
    return properties;
}

/** Stringify and truncate a property value to stay within maxLength characters. */
function clampValue(value: unknown, maxLength: number): unknown {
    if (value === null || value === undefined) return value;
    if (typeof value === "number" || typeof value === "boolean") return value;
    if (typeof value === "string") {
        return value.length > maxLength ? value.slice(0, maxLength) + `... (truncated, ${value.length} chars)` : value;
    }
    try {
        const str = safeStringify(value, undefined, safeStringifyReplacer);
        if (str.length > maxLength) {
            return str.slice(0, maxLength) + `... (truncated, ${str.length} chars)`;
        }
        return str;
    } catch {
        return `[Unserializable: ${typeof value}]`;
    }
}
